import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartLine, Coupon, FeatureKey, Product, Tenant, User } from '@/types';
import { services } from '@/services';
import { demoTenant } from '@/data/tenant';

/* ------------------------------ Tenant ------------------------------ */
interface TenantState {
  tenant: Tenant;
  ready: boolean;
  load: () => Promise<void>;
}
export const useTenantStore = create<TenantState>((set) => ({
  tenant: demoTenant, // sensible default so first paint is never empty
  ready: false,
  load: async () => {
    try {
      set({ tenant: await services.tenant.getCurrent(), ready: true });
    } catch {
      set({ ready: true });
    }
  },
}));

/** Feature-flag check. Every module-specific UI must go through this so tenants can toggle it off. */
export const useFeature = (key: FeatureKey) => useTenantStore((s) => s.tenant.features[key]);

/* ------------------------------ Auth ------------------------------ */
interface AuthState {
  user: User | null;
  ready: boolean;
  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, referralCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (u: User) => void;
}
export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  ready: false,
  init: async () => set({ user: await services.auth.currentUser(), ready: true }),
  login: async (email, password) => set({ user: await services.auth.login({ email, password }) }),
  register: async (name, email, password, referralCode) =>
    set({ user: await services.auth.register({ name, email, password, referralCode }) }),
  logout: async () => {
    await services.auth.logout();
    set({ user: null });
  },
  setUser: (user) => set({ user }),
}));

/* ------------------------------ Cart (client state; persisted locally) ------------------------------ */
export const lineKey = (productId: string, variant?: Record<string, string>) =>
  productId + (variant ? '|' + Object.entries(variant).sort().map(([k, v]) => `${k}=${v}`).join(',') : '');

interface CartState {
  lines: CartLine[];
  coupon?: Coupon;
  add: (p: Product, qty?: number, variant?: Record<string, string>) => void;
  setQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  toggleSaved: (key: string) => void;
  applyCoupon: (c?: Coupon) => void;
  clear: () => void;
}
export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      add: (p, qty = 1, variant) =>
        set((s) => {
          const key = lineKey(p.id, variant);
          const existing = s.lines.find((l) => l.key === key);
          if (existing) {
            return { lines: s.lines.map((l) => (l.key === key ? { ...l, savedForLater: false, quantity: Math.min(l.quantity + qty, l.maxQuantity) } : l)) };
          }
          const line: CartLine = {
            key, productId: p.id, slug: p.slug, name: p.name, price: p.price, compareAtPrice: p.compareAtPrice,
            icon: p.icon, hue: p.hue, image: p.images[0], quantity: Math.min(qty, p.stock, 10), maxQuantity: Math.min(p.stock, 10), variant,
          };
          return { lines: [...s.lines, line] };
        }),
      setQty: (key, qty) =>
        set((s) => ({ lines: s.lines.map((l) => (l.key === key ? { ...l, quantity: Math.max(1, Math.min(qty, l.maxQuantity)) } : l)) })),
      remove: (key) => set((s) => ({ lines: s.lines.filter((l) => l.key !== key) })),
      toggleSaved: (key) => set((s) => ({ lines: s.lines.map((l) => (l.key === key ? { ...l, savedForLater: !l.savedForLater } : l)) })),
      applyCoupon: (coupon) => set({ coupon }),
      clear: () => set({ lines: [], coupon: undefined }),
    }),
    { name: 'mce.cart.v1' },
  ),
);

/* ------------------------------ Theme (light/dark) ------------------------------ */
type ThemeMode = 'light' | 'dark';
interface ThemeState {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
}
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'light',
      toggle: () => set((s) => ({ mode: s.mode === 'light' ? 'dark' : 'light' })),
      setMode: (mode) => set({ mode }),
    }),
    { name: 'mce.theme.v1' },
  ),
);

/* ------------------------------ Toasts ------------------------------ */
export type ToastTone = 'success' | 'error' | 'info';
export interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}
interface ToastState {
  items: ToastItem[];
  push: (t: Omit<ToastItem, 'id'>) => void;
  dismiss: (id: number) => void;
}
let toastId = 0;
export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (t) => {
    const id = ++toastId;
    set((s) => ({ items: [...s.items.slice(-3), { ...t, id }] }));
    setTimeout(() => set((s) => ({ items: s.items.filter((x) => x.id !== id) })), 4200);
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
}));

export const toast = {
  success: (title: string, description?: string) => useToastStore.getState().push({ tone: 'success', title, description }),
  error: (title: string, description?: string) => useToastStore.getState().push({ tone: 'error', title, description }),
  info: (title: string, description?: string) => useToastStore.getState().push({ tone: 'info', title, description }),
};
